import React, { useMemo } from 'react'
import { AppProgressState } from '@components/AppProgressOverlay/AppProgressState'
import {
    DEFAULT_CONFIG,
    NodeAnimationContext,
} from '@components/NodeAnimation/NodeAnimationContext'
import { NodeAnimationLoader } from '@components/NodeAnimation/NodeAnimationLoader'
import { Box, Icon, Stack, Text } from '@ui'
import { publicPageLoginStore } from 'routes/PublicTownPage/usePublicPageLoginFlow'
import { useStore } from 'store/store'
import { Figma } from 'ui/styles/palette'
import { JoinFlowStatus } from 'use-towns-client'

import { RotatingText } from './components/RotatingText'
import { useDummyNodes } from './hooks/useDummyNodes'
import { SetupChecklist } from './SetupChecklist'

type Props = {
    mode:
        | AppProgressState.Joining
        | AppProgressState.InitializingWorkspace
        | AppProgressState.CreatingSpace
        | AppProgressState.CreatingDM
        | AppProgressState.CreatingGDM
        | AppProgressState.LinkingSmartAccount
}

export const SetupAnimation = (props: Props) => {
    const { mode } = props
    const darkMode = useStore((state) => state.getTheme() === 'dark')

    const dummyNodes = useDummyNodes()
    const someNodeUrl = dummyNodes[1].nodeUrl

    const joinStep = publicPageLoginStore((s) => s.joinStatusStep)

    const nodeUrl = useMemo(() => {
        if (mode === AppProgressState.Joining) {
            if (joinStep && joinStep > JoinFlowStatus.JoiningRoom) {
                return someNodeUrl
            } else {
                return 'none'
            }
        } else {
            return someNodeUrl
        }
    }, [joinStep, mode, someNodeUrl])

    const backgroundColorString = useMemo(
        () => (darkMode ? 'hsla(255, 9%, 16%, 1)' : Figma.LightMode.Level1),
        [darkMode],
    )

    const config = useMemo(
        () => ({
            ...DEFAULT_CONFIG,
            nodeUrl,
            darkMode,
            backgroundColorString,
            nodeConnections: dummyNodes,
        }),
        [backgroundColorString, darkMode, dummyNodes, nodeUrl],
    )

    const rotateText = useMemo(
        () =>
            mode === AppProgressState.InitializingWorkspace
                ? [
                      'Connecting to Towns Network',
                      'Downloading message streams',
                      'Setting up your towns',
                  ]
                : mode === AppProgressState.CreatingDM || mode === AppProgressState.CreatingGDM
                  ? [
                        mode === AppProgressState.CreatingDM
                            ? 'Creating encrypted direct message'
                            : 'Creating encrypted group',
                        'Connecting to Towns Network',
                        'Sharing encryption keys',
                    ]
                  : [],
        [mode],
    )

    return (
        <Stack gap centerContent data-testid="setup-animation">
            <NodeAnimationContext.Provider value={config}>
                <NodeAnimationLoader skipPlaceholder animateIntro maxWidth="250" />
            </NodeAnimationContext.Provider>
            {mode === AppProgressState.InitializingWorkspace ? (
                <RotatingText texts={rotateText} />
            ) : mode === AppProgressState.CreatingDM || mode === AppProgressState.CreatingGDM ? (
                <RotatingText texts={rotateText} />
            ) : (
                <JoiningChecklist mode={mode} />
            )}
        </Stack>
    )
}

const JoiningChecklist = (props: { mode: AppProgressState }) => {
    const { mode } = props
    // the following steps are slightly misaligned with the actual steps
    // actual: 1.mint, 2.join default channel 3. done
    // "connecting" and "initializing workspace" are happening in parallel
    // the following steps are more relevant to the user:
    const steps = useMemo(() => {
        if (mode === AppProgressState.LinkingSmartAccount) {
            return ['Linking your smart account']
        }
        return [
            'Minting town membership',
            'Connecting to Towns Network',
            'Downloading message streams',
        ]
    }, [mode])
    const joinStep = publicPageLoginStore((s) => s.joinStatusStep)

    const step = useMemo(() => {
        switch (joinStep) {
            case JoinFlowStatus.MintingMembership:
            case JoinFlowStatus.WaitingForMembership:
                return 0
            case JoinFlowStatus.MembershipMinted:
            case JoinFlowStatus.JoiningRoom:
            case JoinFlowStatus.JoiningAutojoinChannels:
                return 1
            case JoinFlowStatus.Done:
                return 2
            default:
                return 0
        }
    }, [joinStep])

    return <JoiningBox steps={steps} step={step} mode={mode} />
}

function JoiningBox({
    steps,
    step,
    mode,
}: {
    steps: string[]
    step: number
    mode: AppProgressState
}) {
    return (
        <Box width={{ mobile: '100%', desktop: '300' }} gap="lg">
            <SetupChecklist steps={steps} step={step} />
            <Box
                padding
                horizontal
                gap
                centerContent
                rounded="md"
                background="transparent5"
                color="gray2"
            >
                <Icon type="alert" size="square_sm" />
                {mode === AppProgressState.LinkingSmartAccount ? (
                    <Box gap="sm">
                        <Text>{`Linking your smart account is required for paying with card.`}</Text>
                    </Box>
                ) : (
                    <Box gap="sm">
                        <Text>Give it a min...building a</Text>
                        <Text>town takes a village.</Text>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
